/**
 * חברות במשרד — company administration, and the refusal at the limit. OA-0001 §3.11, 08 §7.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * AT THE LIMIT, THE COUNT · THE LIMIT · A ROUTE. NEVER A GENERIC ERROR.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * 08 §7 requires a hard block to return *"a SPECIFIC ACTIONABLE reason with the count, the limit and
 * an upgrade path — never a generic 403, never a silent failure"*, and OA-0001 §3.11 adds the half
 * that matters more: *"Commercial enforcement must not corrupt or delete existing Company data."*
 * Both halves are on this screen, and the second one is written out in the refusal itself — because
 * an office told only "you cannot add a company" reasonably fears that something is about to be
 * taken away.
 *
 * The refusal is a value the SERVER produced (`checkCapacity` in `apps/api/src/domain/capacity.ts`).
 * This screen renders it. It does not compare `current` to `limit` to decide anything: a second
 * implementation of a commercial rule is family F9 with a Hebrew accent, and F9 is two answers to
 * one question drifting apart at their own pace.
 *
 * ── AND THE BUTTON IS STILL THERE ───────────────────────────────────────────────────────────────
 *
 * "הקמת חברה" is not hidden at the limit. A hidden button is presentation, never a control — the
 * gate refuses the write regardless, and hiding the affordance only means the office cannot find out
 * WHY. What changes at the limit is that the refusal is stated before the form is opened rather than
 * after it is filled in.
 */

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { CapacityPanel, CapacityRefusalNotice } from '../components/CapacityPanel';
import { DataTable } from '../components/DataTable';
import { LifecycleChip } from '../components/LifecycleChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { Column } from '../components/DataTable';
import type { CompanySummary } from '../api/contract';
import {
  fixtureCompanyCapacityRefusal,
  fixtureSubscription,
  fixturesEnabled,
} from '../api/fixtures';
import { routes } from '../routes';

const TITLE_HE = 'חברות במשרד';

export function TenantCompaniesScreen(): JSX.Element {
  const ctx = useWorkContext();

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="TENANT" actual={ctx.surface} />;
  }

  // The server's single "visible companies" answer, rendered as given. Nothing is filtered here.
  const companies = ctx.visibleCompanies;

  // The refusal, as the server produced it. `null` when there is room, and the screen shows nothing.
  const refusal = fixturesEnabled() ? fixtureCompanyCapacityRefusal : null;
  const capacity = fixturesEnabled() ? fixtureSubscription.capacity : [];

  const columns: readonly Column<CompanySummary>[] = [
    {
      key: 'name',
      headerHe: 'שם החברה',
      render: (c) => (
        <span className="company-cell">
          <span className="company-cell__name">{c.legalName}</span>
          <span className="company-cell__id" dir="ltr">
            {c.registrationNumber ?? 'ללא ח.פ רשום'}
          </span>
        </span>
      ),
    },
    {
      key: 'lifecycle',
      headerHe: 'מצב החברה',
      render: (c) => <LifecycleChip status={c.lifecycleStatus} />,
    },
    {
      key: 'attention',
      headerHe: 'הערות',
      render: (c) => (c.needsAttention ? <span className="chip chip--attention">דורש טיפול</span> : null),
    },
    {
      key: 'record',
      headerHe: 'כרטיס במשרד',
      /*
        THE ADMINISTRATIVE RECORD, which is a different place from the work surface.
        The row itself opens `/c/{uid}` — where a bookkeeper does this month's work. This column
        opens the client's master record: its legal name, its lifecycle state, its identifiers and
        its source bindings. Keeping them apart is what stops "mark this client as former" from
        sitting one click from "open the trial balance".
      */
      render: (c) => (
        <a className="btn btn--quiet" href={routes.tenantCompany(c.companyUid)}>
          פרטים ומצב
        </a>
      ),
    },
  ];

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'ניהול המשרד', href: routes.tenantAdmin }}
      provenanceHe="מכסה מלאה חוסמת הקמה של חברה נוספת בלבד. חברות קיימות, ההרשאות אליהן והנתונים שבהן אינם משתנים."
    >
      {/* The refusal comes FIRST when there is one: it is the answer to the action the user came for. */}
      {refusal === null ? null : <CapacityRefusalNotice refusal={refusal} />}

      <div className="actions-row">
        <a className="btn btn--primary" href={routes.tenantCompanyOnboarding}>
          הקמת חברה
        </a>
        <a className="btn" href={routes.tenantSubscription}>
          מנוי ומכסות
        </a>
      </div>

      <CapacityPanel capacity={capacity} captionHe="המכסה שנרכשה" />

      <DataTable<CompanySummary>
        captionHe="חברות במשרד"
        columns={columns}
        rows={companies}
        rowKey={(c) => c.companyUid}
        childHref={(c) => routes.companyHome(c.companyUid)}
        filterSummaryHe={`מציג ${companies.length} חברות שיש לך גישה אליהן`}
        emptyStateHe="אין עדיין חברות במשרד."
      />
    </AppShell>
  );
}
